import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { vibeService, VIBE_VISIBILITY_EVENT } from '../../services/VibeService';
import UpdateAnnounceModal from './UpdateAnnounceModal';
import './Sidebar.css';

type NavKey = 'overview' | 'skills' | 'activity' | 'interviews' | 'achievements' | 'settings';

export interface SidebarProps {
  className?: string;
  activeNav: NavKey;
  onSelectNav: (key: Exclude<NavKey, 'settings'>) => void;
  initials: string;
  name: string;
  email?: string;
  avatarUrl?: string;
  position?: string;
  organizationName?: string;
  departmentName?: string;
  preparednessPercent?: number;
  onStartInterview: () => void;
  onLogout: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  upcomingCount?: number;
}

// Helper function to join class names
const cx = (...classes: (string | boolean | undefined)[]) => 
  classes.filter(Boolean).join(' ');

// Icons as components for cleaner code
const ChevronIcon: React.FC<{ expanded?: boolean }> = ({ expanded }) => (
  <svg
    className={cx('sidebar__chevron', expanded && 'sidebar__chevron--expanded')}
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const Sidebar: React.FC<SidebarProps> = ({
  className,
  activeNav,
  onSelectNav,
  initials,
  name,
  email,
  avatarUrl,
  organizationName,
  departmentName,
  onLogout,
  onStartInterview,
  isCollapsed = false,
  onToggleCollapse,
  upcomingCount = 0,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const activeUpcomingCount = upcomingCount;
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    settings: false,
  });
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [isVibeQuit, setIsVibeQuit] = useState(() => {
    return localStorage.getItem('vibe-drawer-quit') === 'true';
  });

  useEffect(() => {
    const handleVibeVisibility = (event: CustomEvent<{ visible: boolean }>) => {
      setIsVibeQuit(!event.detail.visible);
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'vibe-drawer-quit') {
        setIsVibeQuit(e.newValue === 'true');
      }
    };
    window.addEventListener(VIBE_VISIBILITY_EVENT, handleVibeVisibility as EventListener);
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener(VIBE_VISIBILITY_EVENT, handleVibeVisibility as EventListener);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleVibeToggle = () => {
    vibeService.show();
    setIsVibeQuit(false);
  };

  // Check if we're on specific routes
  const isAnalyticsPage = location.pathname === '/analytics';
  const isSettingsPage = location.pathname === '/settings';

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleUserDropdown = () => {
    setShowUserDropdown((prev) => !prev);
  };

  const handleLogout = () => {
    setShowUserDropdown(false);
    onLogout();
  };

  return (
    <>
    <aside className={cx('sidebar', isCollapsed && 'sidebar--collapsed', className)} aria-label="Primary navigation">
      {/* Header/Logo */}
      <div className="sidebar__header" onClick={() => navigate('/candidate')}>
        <div className="sidebar__logo-wrapper">
          <img 
            src="/image_i_do1.png" 
            alt="Luci logo" 
            className="sidebar__logo"
          />
          <div>
            <div className="sidebar__brand-title">Hyper</div>
            <div className="sidebar__brand-subtitle">Career assistant</div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="sidebar__search">
        <svg className="sidebar__search-icon" viewBox="0 0 24 24">
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
        <span>Search</span>
      </div>

      {/* Main Navigation */}
      <nav className="sidebar__nav">
        {/* Dashboard */}
        <button
          type="button"
          className={cx('sidebar__nav-item', activeNav === 'overview' && 'sidebar__nav-item--active')}
          onClick={() => onSelectNav('overview')}
        >
          <div className="sidebar__nav-item-left">
            <svg className="sidebar__nav-icon" viewBox="0 0 24 24">
              <path d="M3 3h7v9H3zM14 3h7v5h-7zM14 12h7v9h-7zM3 16h7v5H3z" />
            </svg>
            <span>Dashboard</span>
          </div>
        </button>

        {/* My Skills */}
        <button
          type="button"
          className={cx('sidebar__nav-item', activeNav === 'skills' && 'sidebar__nav-item--active')}
          onClick={() => onSelectNav('skills')}
        >
          <div className="sidebar__nav-item-left">
            <svg className="sidebar__nav-icon" viewBox="0 0 24 24">
              <path d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span>My Skills</span>
          </div>
          <span className="sidebar__badge">10</span>
        </button>

        {/* My Activity */}
        <button
          type="button"
          className={cx('sidebar__nav-item', activeNav === 'activity' && 'sidebar__nav-item--active')}
          onClick={() => onSelectNav('activity')}
        >
          <div className="sidebar__nav-item-left">
            <svg className="sidebar__nav-icon" viewBox="0 0 24 24">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
            <span>My Activity</span>
          </div>
          {activeUpcomingCount > 0 && (
            <span className="sidebar__badge sidebar__badge--active">{activeUpcomingCount}</span>
          )}
        </button>

        <div className="sidebar__divider" />

        {/* Analytics */}
        <button
          type="button"
          className={cx('sidebar__nav-item', (activeNav === 'achievements' || isAnalyticsPage) && 'sidebar__nav-item--active')}
          onClick={() => navigate('/analytics')}
        >
          <div className="sidebar__nav-item-left">
            <svg className="sidebar__nav-icon" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
            <span>Analytics</span>
          </div>
        </button>

        {/* Settings */}
        <button
          type="button"
          className={cx('sidebar__nav-item', (activeNav === 'settings' || isSettingsPage) && 'sidebar__nav-item--active')}
          onClick={() => navigate('/settings')}
        >
          <div className="sidebar__nav-item-left">
            <svg className="sidebar__nav-icon" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
            </svg>
            <span>Settings</span>
          </div>
          <span className="sidebar__badge sidebar__badge--new">new</span>
        </button>

        {/* Build Interview */}
        <button
          type="button"
          className={cx('sidebar__nav-item', location.pathname === '/build-interview' && 'sidebar__nav-item--active')}
          onClick={() => navigate('/build-interview')}
        >
          <div className="sidebar__nav-item-left">
            <svg className="sidebar__nav-icon" viewBox="0 0 24 24">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <path d="M14 17.5h7M17.5 14v7" strokeLinecap="round" />
            </svg>
            <span>Build Interview</span>
          </div>
        </button>

        {/* Build Resume */}
        <button
          type="button"
          className={cx('sidebar__nav-item', location.pathname === '/build-resume' && 'sidebar__nav-item--active')}
          onClick={() => navigate('/build-resume')}
        >
          <div className="sidebar__nav-item-left">
            <svg className="sidebar__nav-icon" viewBox="0 0 24 24">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
            </svg>
            <span>Build Resume</span>
          </div>
        </button>
       

        {/* Vibe Toggle - shown when drawer has been quit */}
        {isVibeQuit && (
          <button
            type="button"
            className="sidebar__nav-item sidebar__vibe-toggle"
            onClick={handleVibeToggle}
            title="Open Vibe drawer"
          >
            <div className="sidebar__nav-item-left">
              <svg className="sidebar__nav-icon sidebar__nav-icon--vibe" viewBox="0 0 24 24">
                <path d="M12 3l1.912 5.813a1 1 0 00.95.687h6.138l-4.97 3.613a1 1 0 00-.364 1.118L17.577 20l-4.97-3.613a1 1 0 00-1.214 0L6.423 20l1.911-5.769a1 1 0 00-.364-1.118L3 9.5h6.138a1 1 0 00.95-.687L12 3z" />
              </svg>
              <span>Vibe</span>
            </div>
          </button>
        )}

        {/* Take Interviews */}
        <button
          type="button"
          className="sidebar__nav-item sidebar__nav-item--primary"
          onClick={onStartInterview}
        >
          <div className="sidebar__nav-item-left">
            <svg className="sidebar__nav-icon sidebar__nav-icon--interview" viewBox="0 0 24 24">
              {/* Speech bubble - taller */}
              <path d="M21 16a2 2 0 01-2 2H7l-4 4V4a2 2 0 012-2h14a2 2 0 012 2v12z" />
              {/* Question mark - thinner stroke */}
              <path d="M9.5 8a2.5 2.5 0 015 0c0 1.5-2.5 2-2.5 3.5" strokeWidth="1.5" strokeLinecap="round" />
              {/* Question mark dot */}
              <circle cx="12" cy="14.5" r="0.8" fill="currentColor" stroke="none" />
            </svg>
            <span>Practice Interviews</span>
          </div>
        </button>
      </nav>

      {/* Bottom Section */}
      <div className="sidebar__bottom">
        {/* Hamburger Toggle — above What’s new banner */}
        {onToggleCollapse && (
          <button
            type="button"
            className="sidebar__collapse-toggle"
            onClick={onToggleCollapse}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg className="sidebar__collapse-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {isCollapsed ? (
                <>
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </>
              ) : (
                <>
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </>
              )}
            </svg>
            {!isCollapsed && <span>Collapse</span>}
          </button>
        )}

        {!isCollapsed && (
          <button
            type="button"
            className="sidebar__update-tag"
            onClick={() => setShowUpdateModal(true)}
            aria-label="Learn about version 3.2.0 updates"
          >
            <img
              src="/assets/Update_3.2.0.png"
              alt="LUCI version 3.2.0 — New Update"
              className="sidebar__update-tag-img"
            />
          </button>
        )}

        {/* User Profile */}
        <div className="sidebar__user-container">
          <div className="sidebar__user">
            {/* Profile section - opens modal */}
            <div className="sidebar__user-profile" onClick={toggleUserDropdown}>
              {avatarUrl ? (
                <img src={avatarUrl} alt={name} className="sidebar__avatar sidebar__avatar--image" />
              ) : (
                <div className="sidebar__avatar">{initials}</div>
              )}
              <div className="sidebar__user-info">
                <div className="sidebar__user-name">{name}</div>
                <div className="sidebar__user-email">{email}</div>
              </div>
            </div>
            
            {/* Vertical Separator */}
            <div className="sidebar__user-separator" />
            
            {/* Logout Arrow */}
            <button 
              type="button" 
              className="sidebar__user-logout-btn"
              onClick={handleLogout}
              title="Logout"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Note */}
          <div className="sidebar__note">
            Version 3.2.0 —
            Contact us at info@letuscrackit.ai
        </div>
      </div>

    </aside>

      {/* Profile Modal - Rendered via Portal to document.body for proper centering */}
      <UpdateAnnounceModal
        open={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
      />

      {showUserDropdown && ReactDOM.createPortal(
        <>
          <div className="sidebar__modal-overlay" onClick={() => setShowUserDropdown(false)} />
          <div className="sidebar__profile-modal">
            {/* Close Button */}
            <button 
              type="button" 
              className="sidebar__profile-close"
              onClick={() => setShowUserDropdown(false)}
              aria-label="Close profile"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            {/* Profile Header */}
            <div className="sidebar__profile-header">
              {avatarUrl ? (
                <img src={avatarUrl} alt={name} className="sidebar__profile-avatar sidebar__profile-avatar--image" />
              ) : (
                <div className="sidebar__profile-avatar">{initials}</div>
              )}
              <div className="sidebar__profile-info">
                <div className="sidebar__profile-name">{name}</div>
                <div className="sidebar__profile-email">{email}</div>
              </div>
            </div>

            <div className="sidebar__profile-meta">
              <div className="sidebar__profile-meta-row">
                <div className="sidebar__profile-meta-label">Organization</div>
                <p
                  className={
                    organizationName
                      ? 'sidebar__profile-meta-value'
                      : 'sidebar__profile-meta-value sidebar__profile-meta-value--placeholder'
                  }
                >
                  {organizationName || '—'}
                </p>
              </div>
              <div className="sidebar__profile-meta-row">
                <div className="sidebar__profile-meta-label">Department</div>
                <p
                  className={
                    departmentName
                      ? 'sidebar__profile-meta-value'
                      : 'sidebar__profile-meta-value sidebar__profile-meta-value--placeholder'
                  }
                >
                  {departmentName || '—'}
                </p>
              </div>
            </div>

            {/* Divider */}
            <div className="sidebar__profile-divider" />

            {/* Actions */}
            <div className="sidebar__profile-actions">
              <button 
                type="button" 
                className="sidebar__profile-action"
                onClick={() => {
                  setShowUserDropdown(false);
                  navigate('/settings');
                }}
              >
                <svg className="sidebar__profile-action-icon" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
                </svg>
                <span>Edit Profile</span>
              </button>
              <button 
                type="button" 
                className="sidebar__profile-action sidebar__profile-action--logout"
                onClick={handleLogout}
              >
                <svg className="sidebar__profile-action-icon" viewBox="0 0 24 24">
                  <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                <span>Logout</span>
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
};

export default Sidebar;
